import { AnimatePresence, motion } from 'framer-motion';
import { LuBook, LuChevronRight, LuCrown } from 'react-icons/lu';
import { Pill } from '../../design-system/components';
import { formatPercent } from '../../utils/percent';
import { SidebarPathItem } from './SidebarPathItem';
import type { DomainWithPaths } from './types';

interface SidebarDomainItemProps {
  domain: DomainWithPaths;
  isExpanded: boolean;
  currentPathId: number | null;
  onToggle: () => void;
  onPathClick: (pathId: number) => void;
}

// Check if domain contains the current path
function domainContainsPath(domain: DomainWithPaths, pathId: number | null): boolean {
  if (!pathId) return false;
  return domain.paths?.some((p) => p.id === pathId) || false;
}

/**
 * One unit row in the Course navigation sidebar (redesign PR4, course.css):
 * chevron, icon tile, name (it carries the unit number) and lesson count,
 * the progress ring; its
 * chapters open under it in a white inset card. Same toggle and path clicks.
 */
export function SidebarDomainItem({
  domain,
  isExpanded,
  currentPathId,
  onToggle,
  onPathClick,
}: SidebarDomainItemProps) {
  const progress = domain.progress || 0;
  const lessonCount = domain.total_lessons || 0;
  const completedCount = domain.completed_lessons || 0;
  const isActive = domainContainsPath(domain, currentPathId);
  const state = isActive ? 'active' : isExpanded ? 'expanded' : 'idle';

  return (
    <div className="ee-side-unit">
      {/* Domain Header */}
      <button
        type="button"
        onClick={onToggle}
        className={`ee-side-unit__head ee-side-unit__head--${state}`}
      >
        {/* Expand Icon */}
        <motion.span
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="ee-side-unit__chev"
        >
          <LuChevronRight aria-hidden="true" />
        </motion.span>

        {/* Domain Icon (the unit's number is in its name, "01 - intro") */}
        <span
          className={`ee-side-unit__num ${domain.mastered ? 'ee-side-unit__num--gold' : ''}`}
          aria-hidden="true"
        >
          {domain.mastered ? <LuCrown /> : <LuBook />}
        </span>

        {/* Domain Info */}
        <span className="ee-side-unit__info">
          <span className="ee-side-unit__name-row">
            <span className="ee-side-unit__name">{domain.name}</span>
            {domain.mastered ? <Pill variant="done">DONE</Pill> : null}
          </span>
          <span className="ee-side-unit__count">
            {completedCount}/{lessonCount} lessons
          </span>
        </span>

        {/* Progress Circle */}
        <span className="ee-side-ring">
          <svg viewBox="0 0 32 32" aria-hidden="true">
            <circle cx="16" cy="16" r="12" className="ee-side-ring__track" />
            <circle
              cx="16"
              cy="16"
              r="12"
              strokeDasharray={`${progress * 0.754} 75.4`}
              className={`ee-side-ring__fill ${domain.mastered ? 'ee-side-ring__fill--gold' : ''}`}
            />
          </svg>
          <span className="ee-side-ring__label">{formatPercent(progress)}</span>
        </span>
      </button>

      {/* Expanded Paths */}
      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="ee-side-unit__paths-wrap"
          >
            <div className="ee-side-unit__paths">
              {domain.paths?.map((path) => (
                <SidebarPathItem
                  key={path.id}
                  path={path}
                  isActive={path.id === currentPathId}
                  onClick={() => onPathClick(path.id)}
                />
              ))}
              {(!domain.paths || domain.paths.length === 0) && (
                <div className="ee-side-unit__empty">No units available</div>
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
